import React, { useState, useEffect, useMemo } from 'react';
import {
	useGlobalDispatch,
	useGlobalState,
} from '../../../stateProviders/GlobalStateProvider';
import {
	Box,
	Button,
	IconButton,
	MenuItem,
	Select,
	TextField,
	Typography,
	FormLabel,
	Stack,
	CircularProgress,
} from '@mui/material';
import { useApi } from '../../../hooks/useApi';
import { CompanyDiscountsAndProfitUpdateParams } from '../../../types/api/Company';
import { TbTrash } from 'react-icons/tb';
import { useUnsavedFormChanges } from '../../../hooks/useUnsavedFormChanges';
import { useUnsavedChangesContext } from '../../../contexts/UnsavedChangesContext';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import { toastError } from 'src/Toaster/Toast';

interface Discount {
	discountName: string;
	discountPercentage: number | string;
	orderIndex: number;
}

interface DiscountEntryProps {
	localDiscounts: Discount[];
	onSave: (
		discounts: { discountName: string; discountPercentage: number }[]
	) => Promise<void>;
	setLocalDiscounts: (
		discounts: { discountName: string; discountPercentage: number | string }[]
	) => void;
	isSaving: boolean;
	markAsClean: () => void;
}

const maxDiscounts = 3;
const isValidDiscount = (discount: Discount): boolean => {
	const hasName = discount.discountName?.trim() !== '';
	const hasPercentage =
		discount.discountPercentage !== '' &&
		discount.discountPercentage !== null &&
		discount.discountPercentage !== undefined;

	return hasName && hasPercentage;
};

const filterNonEmptyDiscounts = (discounts: Discount[]): Discount[] => {
	return discounts.filter(isValidDiscount);
};

const DiscountEntry = ({
	localDiscounts,
	onSave,
	setLocalDiscounts,
	isSaving,
	markAsClean,
}: DiscountEntryProps) => {
	const handleDiscountChange = (
		index: number,
		field: 'discountName' | 'discountPercentage',
		value: string
	) => {
		const newDiscounts = [...localDiscounts];
		newDiscounts[index] = {
			...newDiscounts[index],
			[field]: value,
		};
		setLocalDiscounts(newDiscounts);
	};

	const addDiscount = () => {
		setLocalDiscounts([
			...localDiscounts,
			{
				discountName: '',
				discountPercentage: '',
				orderIndex: localDiscounts.length + 1,
			},
		]);
	};

	const removeDiscount = async (index: number) => {
		let updatedDiscounts = localDiscounts.filter((_, i) => i !== index);
		updatedDiscounts.sort((a, b) => a.orderIndex - b.orderIndex);

		updatedDiscounts = updatedDiscounts.map((discount, i) => ({
			...discount,
			orderIndex: i + 1,
		}));

		setLocalDiscounts(updatedDiscounts);
		const discountsToSave = updatedDiscounts.map((discount) => ({
			...discount,
			discountPercentage:
				typeof discount.discountPercentage === 'string'
					? discount.discountPercentage === ''
						? 0
						: parseFloat(discount.discountPercentage)
					: discount.discountPercentage,
		}));

		await onSave(discountsToSave);
	};

	const handleSave = async () => {
		// Filter out empty discounts (those without a name or percentage)
		const nonEmptyDiscounts = filterNonEmptyDiscounts(localDiscounts);
		if (nonEmptyDiscounts.length > maxDiscounts) {
			toastError({
				description: `You can only have a maximun of ${maxDiscounts} discounts`,
			});
			return;
		}

		const nameCounts = nonEmptyDiscounts.reduce((acc, discount) => {
			acc[discount.discountName] = (acc[discount.discountName] || 0) + 1;
			return acc;
		}, {});

		const duplicates = Object.keys(nameCounts).filter(
			(name) => nameCounts[name] > 1
		);

		if (duplicates.length) {
			toastError({
				description: `Discounts cannot have the same name: ${duplicates[0]}`,
			});

			return;
		}

		const discountsToSave = nonEmptyDiscounts.map((discount) => ({
			...discount,
			discountPercentage:
				typeof discount.discountPercentage === 'string'
					? discount.discountPercentage === ''
						? 0
						: parseFloat(discount.discountPercentage)
					: discount.discountPercentage,
		}));

		await onSave(discountsToSave);
	};

	const moveDiscountUp = (index) => {
		const currentDiscount = localDiscounts[index];

		if (!currentDiscount) return;

		const prevDiscount = localDiscounts.find(
			(discount) => discount.orderIndex == currentDiscount.orderIndex - 1
		);

		if (!prevDiscount) return;

		const newDiscounts = localDiscounts.map((discount) => {
			if (discount.discountName == currentDiscount.discountName) {
				return { ...discount, orderIndex: prevDiscount.orderIndex };
			} else if (discount.discountName == prevDiscount.discountName) {
				return { ...discount, orderIndex: currentDiscount.orderIndex };
			}

			return discount;
		});

		newDiscounts.sort((a, b) => a.orderIndex - b.orderIndex);

		setLocalDiscounts(newDiscounts);
	};

	const moveDiscountDown = (index) => {
		const currentDiscount = localDiscounts[index];

		if (!currentDiscount) return;

		const nextDiscount = localDiscounts.find(
			(discount) => discount.orderIndex == currentDiscount.orderIndex + 1
		);

		if (!nextDiscount) return;

		const newDiscounts = localDiscounts.map((discount) => {
			if (discount.discountName == currentDiscount.discountName) {
				return { ...discount, orderIndex: nextDiscount.orderIndex };
			} else if (discount.discountName == nextDiscount.discountName) {
				return { ...discount, orderIndex: currentDiscount.orderIndex };
			}

			return discount;
		});

		newDiscounts.sort((a, b) => a.orderIndex - b.orderIndex);

		setLocalDiscounts(newDiscounts);
	};
	const hasValidDiscounts = localDiscounts.some(isValidDiscount);

	return (
		<Box display="flex" flexDirection="column" gap={2.5} flex={1}>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				mb={2}
			>
				<Stack spacing={1}>
					<Typography variant="h2" fontWeight={700}>
						Discounts
					</Typography>
					<Typography variant="subtitle1" color="text.secondary">
						Define and reorder upto 3 discounts available for quotes
					</Typography>
				</Stack>
				<Button
					variant="contained"
					color="success"
					onClick={addDiscount}
					disabled={isSaving || localDiscounts.length >= maxDiscounts}
				>
					Add New Discount
				</Button>
			</Stack>
			<Box
				display="grid"
				gridTemplateColumns={{ xs: '1fr', sm: '5% 45% 45%' }}
				gap={2}
			>
				{localDiscounts.length ? (
					localDiscounts.map(({ discountName, discountPercentage }, i) => (
						<React.Fragment key={i}>
							<Box
								sx={{
									width: '30px',
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
									gap: '10px',
								}}
							>
								<IconButton
									sx={{
										borderRadius: '50%',
										backgroundColor: '#F5F5F5',
										'&:hover': {
											background: '#636B74',
										},
									}}
									disabled={i == 0}
									onClick={() => moveDiscountUp(i)}
								>
									<NorthIcon sx={{ fontSize: '20px' }} />
								</IconButton>

								<IconButton
									sx={{
										borderRadius: '50%',
										backgroundColor: '#F5F5F5',
										'&:hover': {
											background: '#636B74',
										},
									}}
									disabled={localDiscounts.length - 1 == i}
									onClick={() => moveDiscountDown(i)}
								>
									<SouthIcon sx={{ fontSize: '20px' }} />
								</IconButton>
							</Box>

							<Box display="flex" flexDirection="column">
								<FormLabel htmlFor={`discountName-${i}`}>
									Discount name
								</FormLabel>
								<TextField
									id={`discountName-${i}`}
									name={`discountName-${i}`}
									variant="outlined"
									value={discountName}
									onChange={(e) =>
										handleDiscountChange(i, 'discountName', e.target.value)
									}
									disabled={isSaving}
									fullWidth
									placeholder="Enter Discount Name"
								/>
							</Box>

							<Box display="flex" flexDirection="column">
								<FormLabel htmlFor={`discountPercentage-${i}`}>
									Discount %
								</FormLabel>
								<Box display="flex" alignItems="center" gap={1}>
									<TextField
										id={`discountPercentage-${i}`}
										type="number"
										name={`discountPercentage-${i}`}
										variant="outlined"
										slotProps={{ htmlInput: { min: 0, max: 100 } }}
										value={discountPercentage || ''}
										placeholder="Enter Discount Percentage"
										onChange={(e) =>
											handleDiscountChange(
												i,
												'discountPercentage',
												e.target.value
											)
										}
										disabled={isSaving}
										fullWidth
									/>
									<IconButton
										color="error"
										onClick={() => removeDiscount(i)}
										edge="end"
										disabled={isSaving}
										aria-label="Remove discount"
									>
										<TbTrash />
									</IconButton>
								</Box>
							</Box>
						</React.Fragment>
					))
				) : (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ gridColumn: '1 / -1' }}
					>
						No discounts added.
					</Typography>
				)}
			</Box>
			<Button
				variant="contained"
				color="success"
				onClick={handleSave}
				disabled={isSaving || !hasValidDiscounts}
				sx={{
					fontWeight: 'bold',
					alignSelf: 'flex-start',
					minWidth: 120,
				}}
			>
				{isSaving ? (
					<Stack direction="row" alignItems="center" gap={1}>
						<CircularProgress size={20} color="inherit" />
						<Typography variant="xs" component="span" fontWeight={600}>
							Saving...
						</Typography>
					</Stack>
				) : (
					'Save Discounts'
				)}
			</Button>
		</Box>
	);
};

interface MarginEntryProps {
	profitType: string;
	profitPercentage: string;
	onSave: (profitType: string, profitPercentage: string) => Promise<void>;
	onChange: (profitType: string, profitPercentage: string) => void;
	isSaving: boolean;
}

const MarginEntry = ({
	profitType,
	profitPercentage,
	onSave,
	onChange,
	isSaving,
}: MarginEntryProps) => {
	const handleSave = async () => {
		await onSave(profitType, profitPercentage);
	};

	return (
		<Box display="flex" flexDirection="column" gap={2.5} flex={1}>
			<Stack spacing={1}>
				<Typography variant="h2" fontWeight={700}>
					Margin
				</Typography>
				<Typography variant="subtitle1" color="text.secondary">
					Set your sell price to the customer
				</Typography>
			</Stack>
			<Box
				display="grid"
				gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
				gap={2}
			>
				<Stack justifyContent="space-between">
					<FormLabel htmlFor="profitType" sx={{ mb: 1 }}>
						Do you want to use mark up or margin to determine your sell price to
						the customer?
					</FormLabel>
					<Select
						id="profitType"
						name="profitType"
						variant="outlined"
						fullWidth
						disabled={isSaving}
						labelId="demo-simple-select-label"
						value={profitType}
						onChange={(e) => onChange(e.target.value, profitPercentage)}
					>
						<MenuItem value={'margin'}>Margin</MenuItem>
						<MenuItem value={'markup'}>Markup</MenuItem>
					</Select>
				</Stack>
				<Stack justifyContent="space-between">
					<FormLabel htmlFor="profitPercentage" sx={{ mb: 1 }}>
						How much margin % do you want on your projects?
					</FormLabel>
					<TextField
						id="profitPercentage"
						type="number"
						name="profitPercentage"
						variant="outlined"
						slotProps={{ htmlInput: { min: 0, max: 100 } }}
						value={profitPercentage}
						onChange={(e) => onChange(profitType, e.target.value)}
						disabled={isSaving}
						fullWidth
					/>
				</Stack>
			</Box>
			<Button
				variant="contained"
				color="success"
				onClick={handleSave}
				disabled={isSaving}
				sx={{
					fontWeight: 'bold',
					alignSelf: 'flex-start',
					minWidth: 120,
				}}
			>
				{isSaving ? (
					<Stack direction="row" alignItems="center" gap={1}>
						<CircularProgress size={20} color="inherit" />
						Saving...
					</Stack>
				) : (
					'Save Margin'
				)}
			</Button>
		</Box>
	);
};

export const DiscountsAndProfit = () => {
	const globalState = useGlobalState();
	const globalDispatch = useGlobalDispatch();
	const [localDiscounts, setLocalDiscounts] = useState<Discount[]>([]);
	const [profitType, setProfitType] = useState(
		globalState.company?.profitType || ''
	);
	const [profitPercentage, setProfitPercentage] = useState(
		globalState.company?.profitPercentage || ''
	);
	const [isSavingDiscounts, setIsSavingDiscounts] = useState(false);
	const [isSavingMargin, setIsSavingMargin] = useState(false);
	const api = useApi();

	useEffect(() => {
		const discounts = globalState.company?.discounts;
		if (discounts && discounts.length) {
			const newDiscounts = [...discounts];

			newDiscounts.sort((a, b) => a.orderIndex - b.orderIndex);
			setLocalDiscounts(newDiscounts);
		} else {
			setLocalDiscounts([
				{ discountName: '', discountPercentage: '', orderIndex: 1 },
				{ discountName: '', discountPercentage: '', orderIndex: 2 },
				{ discountName: '', discountPercentage: '', orderIndex: 3 },
			]);
		}
	}, [globalState.company?.discounts]);

	const savedDiscountsData = useMemo(
		() => ({
			discounts: JSON.stringify(globalState.company?.discounts || []),
		}),
		[globalState.company?.discounts]
	);

	const currentDiscountsData = useMemo(
		() => ({
			discounts: JSON.stringify(localDiscounts),
		}),
		[localDiscounts]
	);

	const savedProfitData = useMemo(
		() => ({
			profitType: globalState.company?.profitType || '',
			profitPercentage: String(globalState.company?.profitPercentage || ''),
		}),
		[globalState.company?.profitType, globalState.company?.profitPercentage]
	);

	const currentProfitData = useMemo(
		() => ({
			profitType,
			profitPercentage: String(profitPercentage),
		}),
		[profitType, profitPercentage]
	);

	const { isDirty: isDiscountsDirty, markAsClean: markDiscountsClean } =
		useUnsavedFormChanges(currentDiscountsData, savedDiscountsData);
	const { isDirty: isProfitDirty, markAsClean: markProfitClean } =
		useUnsavedFormChanges(currentProfitData, savedProfitData);
	const { setHasUnsavedChanges, setOnSaveCallback } =
		useUnsavedChangesContext();

	const isDirty = isDiscountsDirty || isProfitDirty;

	useEffect(() => {
		setHasUnsavedChanges(isDirty);
	}, [isDirty, setHasUnsavedChanges]);

	useEffect(() => {
		if (isDirty) {
			setOnSaveCallback(() => async () => {
				if (isDiscountsDirty) {
					await saveDiscounts(localDiscounts as any);
				}
				if (isProfitDirty) {
					await saveMargin(profitType, String(profitPercentage));
				}
			});
		} else {
			setOnSaveCallback(null);
		}
	}, [
		isDirty,
		isDiscountsDirty,
		isProfitDirty,
		localDiscounts,
		profitType,
		profitPercentage,
	]);

	useEffect(() => {
		return () => {
			setHasUnsavedChanges(false);
			setOnSaveCallback(null);
		};
	}, [setHasUnsavedChanges, setOnSaveCallback]);

	useEffect(() => {
		const localDiscountsHash = JSON.stringify(localDiscounts);
		const globalDiscountsHash = JSON.stringify(
			globalState.company?.discounts || []
		);

		if (localDiscountsHash === globalDiscountsHash && isDiscountsDirty) {
			markDiscountsClean();
		}
	}, [
		localDiscounts,
		globalState.company?.discounts,
		isDiscountsDirty,
		markDiscountsClean,
	]);

	useEffect(() => {
		const localProfitHash = JSON.stringify({
			profitType,
			profitPercentage: String(profitPercentage),
		});
		const globalProfitHash = JSON.stringify({
			profitType: globalState.company?.profitType || '',
			profitPercentage: String(globalState.company?.profitPercentage || ''),
		});

		if (localProfitHash === globalProfitHash && isProfitDirty) {
			markProfitClean();
		}
	}, [
		profitType,
		profitPercentage,
		globalState.company?.profitType,
		globalState.company?.profitPercentage,
		isProfitDirty,
		markProfitClean,
	]);

	const saveDiscounts = async (newDiscounts: Discount[]) => {
		if (!globalState.company?._id) return;
		const nonEmptyDiscounts = filterNonEmptyDiscounts(newDiscounts);
		if (nonEmptyDiscounts.length > maxDiscounts) {
			toastError({
				description: `You can only have a maximun of ${maxDiscounts} discounts`,
			});
			return;
		}

		const nameCounts = nonEmptyDiscounts.reduce((acc, discount) => {
			acc[discount.discountName] = (acc[discount.discountName] || 0) + 1;
			return acc;
		}, {});

		const duplicates = Object.keys(nameCounts).filter(
			(name) => nameCounts[name] > 1
		);

		if (duplicates.length) {
			toastError({
				description: `Discounts cannot have the same name: ${duplicates[0]}`,
			});

			return;
		}

		setIsSavingDiscounts(true);
		try {
			await api.company.updateDiscountsAndProfitSettings(
				globalState.company._id,
				{
					discounts: nonEmptyDiscounts,
				} as CompanyDiscountsAndProfitUpdateParams
			);
			const updatedCompany = await api.company.getCurrent();
			//setLocalDiscounts(updatedCompany.discounts || []);
			setProfitType(updatedCompany.profitType || '');
			setProfitPercentage(updatedCompany.profitPercentage || '');
			globalDispatch({ company: updatedCompany });
		} catch (error) {
			console.error(error);
		} finally {
			setIsSavingDiscounts(false);
		}
	};

	const saveMargin = async (
		newProfitType: string,
		newProfitPercentage: string
	) => {
		if (!globalState.company?._id) return;
		setIsSavingMargin(true);
		try {
			await api.company.updateDiscountsAndProfitSettings(
				globalState.company._id,
				{
					profitType: newProfitType,
					profitPercentage: Number(newProfitPercentage),
				} as CompanyDiscountsAndProfitUpdateParams
			);
			const updatedCompany = await api.company.getCurrent();

			setProfitType(updatedCompany.profitType || '');
			setProfitPercentage(updatedCompany.profitPercentage || '');
			globalDispatch({ company: updatedCompany });
		} finally {
			setIsSavingMargin(false);
		}
	};

	return (
		<Box
			display="flex"
			flexDirection="column"
			maxWidth={{ xs: '100%', sm: 600, md: 800, lg: 1000 }}
			rowGap={{ xs: 3, sm: 4 }}
		>
			<DiscountEntry
				localDiscounts={localDiscounts}
				onSave={saveDiscounts}
				setLocalDiscounts={setLocalDiscounts}
				isSaving={isSavingDiscounts}
				markAsClean={markDiscountsClean}
			/>
			<MarginEntry
				profitType={profitType}
				profitPercentage={String(profitPercentage)}
				onSave={saveMargin}
				onChange={(newProfitType, newProfitPercentage) => {
					setProfitType(newProfitType);
					setProfitPercentage(newProfitPercentage);
				}}
				isSaving={isSavingMargin}
			/>
		</Box>
	);
};
